// src/features/subscription/context/SubscriptionContext.jsx
// Thin context wrapper around subscription services.
// On native: RevenueCat SDK is source of truth for isPremium.
// On web: Firestore subscriptionTier (written by RevenueCat webhook) is source of truth.

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import * as subscriptionService from '../services/subscriptionService';
import logger from '@/shared/utils/core/logger';
import { setPostHogUserProperties } from '@/shared/utils/analytics/posthog';

const SubscriptionContext = createContext({
  isLoading: true,
  isPremium: false,
  isTrial: false,
  trialEndsAt: null,
  entryCount: 0,
  isOverFreeLimit: false,
  packages: [],
  purchase: async () => {},
  restore: async () => {},
  openPaywall: () => {},
  presentCustomerCenter: async () => {},
});

export function SubscriptionProvider({ children }) {
  const { user: authUser, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [entryCount, setEntryCount] = useState(0);
  const [packages, setPackages] = useState([]);

  // ── Paywall navigation ────────────────────────────────────────────────────
  // On native: present the RevenueCat paywall directly.
  // On web: dispatch event picked up by _layout.jsx to navigate to /paywall.
  const openPaywall = useCallback(async () => {
    if (subscriptionService.isNative()) {
      const result = await subscriptionService.presentPaywall();
      // Customer info listener will update isPremium if they subscribed
      if (result?.purchaseMade) {
        logger.debug('[SubscriptionContext] Purchase made via paywall');
      }
    } else {
      window.dispatchEvent(new CustomEvent('navigate-to-paywall'));
    }
  }, []);

  // ── Customer Center ────────────────────────────────────────────────────────
  const presentCustomerCenter = useCallback(async () => {
    if (subscriptionService.isNative()) {
      await subscriptionService.presentCustomerCenter();
    } else {
      // Web: navigate to in-app subscription manager page.
      window.dispatchEvent(new CustomEvent('navigate-to-subscription-manage'));
    }
  }, []);

  // ── Native: RevenueCat customer info listener ─────────────────────────────
  // On native, this is the primary source of truth for isPremium.
  // Fires immediately with current state, then on every purchase/restore/expiry.
  useEffect(() => {
    if (!authUser || !subscriptionService.isNative()) return;

    // Log in with Firebase UID so RevenueCat ties purchases to this user.
    // configureRevenueCat was already called on app init; this just adds logIn.
    subscriptionService.configureRevenueCat(authUser.uid);

    // Fetch current state immediately (don't wait for listener to fire)
    subscriptionService.getCustomerInfo().then((info) => {
      if (!info) return;
      const { isPremium: p, isTrial: t, trialEndsAt: e } = subscriptionService.parseCustomerInfo(info);
      setIsPremium(p);
      setIsTrial(t);
      setTrialEndsAt(e);
      setIsLoading(false);
    }).catch((error) => {
      logger.error('[SubscriptionContext] RevenueCat init failed:', error);
      setIsLoading(false);
    });

    // Real-time listener for subscription changes
    const cleanup = subscriptionService.addCustomerInfoUpdateListener(
      ({ isPremium: p, isTrial: t, trialEndsAt: e }) => {
        setIsPremium(p);
        setIsTrial(t);
        setTrialEndsAt(e);
        logger.debug('[SubscriptionContext] customerInfo updated — isPremium:', p);
      }
    );

    return cleanup;
  }, [authUser?.uid]);

  // ── Web: Firestore subscription status listener ───────────────────────────
  // Used when RevenueCat SDK is unavailable (web). Also serves as a fallback
  // confirmation on native (webhook updates Firestore within seconds of purchase).
  useEffect(() => {
    if (authLoading || !authUser) {
      if (!authLoading) {
        setIsPremium(false);
        setIsTrial(false);
        setTrialEndsAt(null);
        setIsLoading(false);
      }
      return;
    }

    // On native, RevenueCat listener handles isPremium — Firestore is backup only.
    // On web, Firestore is the only source so we must set state from it.
    const unsub = subscriptionService.subscribeToSubscriptionStatus(
      authUser.uid,
      ({ isPremium: p, isTrial: t, trialEndsAt: e }) => {
        if (!subscriptionService.isNative()) {
          // Web: Firestore is the only source of truth
          setIsPremium(p);
          setIsTrial(t);
          setTrialEndsAt(e);
          setIsLoading(false);
        } else {
          // Native: Firestore is secondary. Only update if Firestore shows premium
          // and RevenueCat hasn't confirmed yet (avoids false-free flicker).
          if (p) setIsPremium(true);
        }
      },
      () => {
        if (!subscriptionService.isNative()) {
          setIsPremium(false);
          setIsLoading(false);
        }
      }
    );

    return unsub;
  }, [authUser?.uid, authLoading]);

  // ── Entry count listener ───────────────────────────────────────────────────
  useEffect(() => {
    if (authLoading) return;

    if (!authUser) {
      setEntryCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const unsub = subscriptionService.subscribeToEntryCount(
      authUser.uid,
      (count) => {
        setEntryCount(count);
        setIsLoading(false);
      },
      () => {
        setEntryCount(0);
        setIsLoading(false);
      }
    );

    return unsub;
  }, [authUser?.uid, authLoading]);

  // ── Sync subscription state to PostHog user properties ────────────────────
  useEffect(() => {
    if (!authUser || isLoading) return;
    setPostHogUserProperties({
      plan: isPremium ? 'pro' : 'free',
      is_trial: isTrial,
      entry_count: entryCount,
      is_over_free_limit: subscriptionService.isOverFreeLimit(entryCount, isPremium),
    });
  }, [authUser?.uid, isPremium, isTrial, entryCount, isLoading]);

  // ── Load offerings ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authUser || !subscriptionService.isNative()) return;
    subscriptionService.getPackages().then(setPackages).catch(() => {
      logger.warn('[SubscriptionContext] Failed to load packages');
    });
  }, [authUser?.uid]);

  // ── Purchase / restore ─────────────────────────────────────────────────────
  const purchase = useCallback(async (pkg) => {
    const result = await subscriptionService.purchase(pkg);
    // Only update state if we have verified customerInfo — never optimistically set premium
    // without proof. The customer info listener (Firestore or RC) will confirm within seconds.
    if (result?.customerInfo) {
      const parsed = subscriptionService.parseCustomerInfo(result.customerInfo);
      setIsPremium(parsed.isPremium);
      setIsTrial(parsed.isTrial);
      setTrialEndsAt(parsed.trialEndsAt);
    }
    return result;
  }, []);

  const restore = useCallback(async () => {
    const result = await subscriptionService.restore();
    if (!result) {
      throw new Error('Restore failed — no response from RevenueCat');
    }
    if (result?.customerInfo) {
      const parsed = subscriptionService.parseCustomerInfo(result.customerInfo);
      setIsPremium(parsed.isPremium);
      setIsTrial(parsed.isTrial);
      setTrialEndsAt(parsed.trialEndsAt);
    } else {
      throw new Error('Could not verify purchase status — please try again');
    }
    return result;
  }, []);

  const isOverFreeLimit = subscriptionService.isOverFreeLimit(entryCount, isPremium);

  const value = useMemo(() => ({
    isLoading,
    isPremium,
    isTrial,
    trialEndsAt,
    entryCount,
    isOverFreeLimit,
    packages,
    purchase,
    restore,
    openPaywall,
    presentCustomerCenter,
  }), [isLoading, isPremium, isTrial, trialEndsAt, entryCount, isOverFreeLimit, packages, purchase, restore, openPaywall, presentCustomerCenter]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscriptionContext() {
  return useContext(SubscriptionContext);
}
